import {
  cardWatchSuggestionOf,
  type ActionCardSurface,
} from '@littlebird/action-cards';
import { getSDK, type ActionCard } from '@littlebird/sdk';
import { useEffect } from 'react';

import {
  actionCardAnalyticsContext,
  actionCardAnalyticsSnapshot,
  operationId,
  trackCardAction,
} from '@/features/action-cards/analytics';
import { actionCardCommands } from '@/features/action-cards/commands';

import {
  actionCardEventSnapshot,
  trackCardCompletedSnapshot,
  trackCardWatchSuggestion,
} from '../analytics';

import type { FeedCardWatch } from './FeedCardView';

type CardWatchOptions = {
  reported: ActionCardSurface;
  todo: boolean;
  onDismiss: () => void;
  cardExpanded?: boolean;
};

// What the card's watcher says, and the user's answers to its suggestion
export const useCardWatch = (
  card: ActionCard,
  { reported, todo, onDismiss, cardExpanded = false }: CardWatchOptions
): FeedCardWatch => {
  // React Compiler would cache what this reads from the stable MobX card and miss the watcher's updates
  'use no memo';
  const suggestion = cardWatchSuggestionOf(card);
  const suggestionKind = suggestion?.kind;

  useEffect(() => {
    if (suggestionKind)
      trackCardWatchSuggestion(
        'CARD_WATCH_SUGGESTION_SHOWN',
        card,
        suggestionKind,
        reported,
        actionCardAnalyticsContext(reported, {
          card_expanded: cardExpanded,
        })
      );
  }, [card, cardExpanded, suggestionKind, reported]);

  // A todo is ticked off rather than marked done elsewhere, so it keeps its own check animation
  const accept = (acknowledge: () => void) => {
    if (!suggestion) return;
    const context = actionCardAnalyticsContext(reported, {
      card_expanded: cardExpanded,
    });
    trackCardWatchSuggestion(
      'CARD_WATCH_SUGGESTION_ACCEPTED',
      card,
      suggestion.kind,
      reported,
      context
    );
    if (suggestion.kind === 'dismiss') onDismiss();
    else if (suggestion.kind === 'working')
      void actionCardCommands.moveWatchedToWorking(card.id);
    else if (todo) acknowledge();
    else {
      const analytics = actionCardEventSnapshot(card, context);
      void actionCardCommands.completeExternally(card.id).then((outcome) => {
        const updated = getSDK().stores.actionCards.get(card.id);
        if (
          outcome.status === 'succeeded' &&
          updated &&
          (updated.status === 'completed' ||
            updated.status === 'completed_externally')
        )
          trackCardCompletedSnapshot(
            analytics,
            'watch_suggestion',
            updated.status,
            'server_terminal'
          );
      });
    }
  };

  const keep = () => {
    if (!suggestion) return;
    trackCardWatchSuggestion(
      'CARD_WATCH_SUGGESTION_KEPT',
      card,
      suggestion.kind,
      reported,
      actionCardAnalyticsContext(reported, {
        card_expanded: cardExpanded,
      })
    );
    const context = actionCardAnalyticsContext(reported, {
      card_expanded: cardExpanded,
    });
    const operation = operationId();
    const analytics = actionCardAnalyticsSnapshot(card, context);
    trackCardAction('watch_keep', analytics, 'submitted', operation);
    // Keeping a card against the Working suggestion says the user is not working on it
    const keepCard =
      suggestion.kind === 'working'
        ? actionCardCommands.notWorkingOnWatched
        : actionCardCommands.keepWatched;
    void keepCard(card.id).then(
      (outcome) =>
        trackCardAction(
          'watch_keep',
          analytics,
          outcome.status === 'succeeded' ? 'succeeded' : 'failed',
          operation,
          outcome.status === 'succeeded'
            ? undefined
            : { outcomeStatus: 'refused' }
        ),
      () =>
        trackCardAction('watch_keep', analytics, 'failed', operation, {
          outcomeStatus: 'error',
        })
    );
  };

  return {
    suggestion,
    accept,
    keep,
  };
};
